import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/InputDefault";
import { Textarea } from "@/components/ui/textarea";
import { Edit, X, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { updateTypeElement, deleteTypeElement } from "@/lib/stores/typesStore";
import { TypeElement } from "@/lib/stores/typesStore";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface TypeElementBadgeProps {
  element: TypeElement;
  typeId: string;
  onRefresh: () => void;
}

interface EditingElement {
  name: string;
  description: string;
}

export default function TypeElementBadge({ element, typeId, onRefresh }: TypeElementBadgeProps) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();

  // State for editing this specific element
  const [isEditing, setIsEditing] = useState(false);
  const [editingElement, setEditingElement] = useState<EditingElement>({
    name: element.name,
    description: element.description || "",
  });

  // Handle element operations
  const handleUpdateElement = async () => {
    if (!editingElement.name.trim()) {
      toast({
        title: "Error",
        description: "Please provide a name for the element",
        variant: "destructive",
      });
      return;
    }

    try {
      await updateTypeElement(
        {
          id: element.id,
          type_id: typeId,
          name: editingElement.name.trim(),
          description: editingElement.description.trim() || undefined,
        },
        toast
      );

      setIsEditing(false);
      
      toast({
        title: "Success",
        description: "Element updated successfully",
      });
      
      onRefresh();
    } catch (error) {
      console.error("Error updating element:", error);
      toast({
        title: "Error",
        description: "Failed to update element. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteElement = async () => {
    try {
      await deleteTypeElement(element.id, typeId, toast);
      
      toast({
        title: "Success",
        description: "Element deleted successfully",
      });
      
      onRefresh();
    } catch (error) {
      console.error("Error deleting element:", error);
      toast({
        title: "Error",
        description: "Failed to delete element. Please try again.",
        variant: "destructive",
      });
    }
  };

  const startEditing = () => {
    setIsEditing(true);
    setEditingElement({
      name: element.name,
      description: element.description || "",
    });
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditingElement({
      name: element.name,
      description: element.description || "",
    });
  };

  if (isEditing) {
    return (
      <div className="bg-white border border-gray-300 rounded-lg p-3 space-y-3 min-w-60">
        <Input
          value={editingElement.name}
          onChange={(e) => setEditingElement({ ...editingElement, name: e.target.value })}
          placeholder={t('platform.cards.widgets.element_name_placeholder')}
          size={8}
        />
        <Textarea
          value={editingElement.description}
          onChange={(e) => setEditingElement({ ...editingElement, description: e.target.value })}
          placeholder={t('platform.common.description')}
          rows={2}
        />
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={handleUpdateElement}
            className="flex items-center gap-2"
          >
            <Save className="h-3 w-3" />
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={cancelEditing}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative group">
      <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-grey-G3 text-grey-G1 text-xs font-medium relative">
        {/* Element content */}
        <div className="w-1.5 h-1.5 rounded-full bg-grey-G1 bg-opacity-60" />
        <span className="text-xs">{element.name}</span>
        
        {/* Action buttons inside badge */}
        <div className="flex items-center gap-0.5 ml-1">
          <button
            onClick={startEditing}
            className="h-4 w-4 flex items-center justify-center rounded-full bg-grey-G3 bg-opacity-20 hover:bg-opacity-30 transition-all"
            title={t('platform.cards.widgets.edit_element')}
          >
            <Edit className="h-2.5 w-2.5" />
          </button>
          <button
            onClick={handleDeleteElement}
            className="h-4 w-4 flex items-center justify-center rounded-full bg-grey-G3 bg-opacity-20 hover:bg-opacity-30 transition-all"
            title={t('platform.cards.widgets.delete_element')}
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </div>
        
        {/* Tooltip for description */}
        {element.description && (
          <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
            {element.description}
          </div>
        )}
      </div>
    </div>
  );
}
